import { StandardLonghandProperties } from 'csstype'

export interface Position {
  wide: Pick<StandardLonghandProperties, 'top' | 'left' | 'right' | 'bottom'>
  narrow?: Pick<StandardLonghandProperties, 'top' | 'left' | 'right' | 'bottom'>
}

export type CityLinksPrefecturesKeys =
  | 'osaka'
  | 'kyoto'
  | 'fukuoka'
  | 'tokyo'
  | 'okayama'
  | 'shizuoka'
  | 'nara'
  | 'hyogo'
  | 'tochigi'
  | 'gunma'
  | 'hiroshima'
  | 'miyagi'
  | 'aichi'
  | 'ibaraki'
  | 'saitama'
  | 'kanagawa'
  | 'chiba'

export const cityLinksPrefectures: Record<
  CityLinksPrefecturesKeys,
  {
    image: { path: string; size: { width: number; height: number } }
    regions: { name: string; code: string; position: Position }[]
  }
> = {
  tokyo: {
    image: {
      path: 'https://s3.lifedot.jp/uploads/ending/path/67794/tokyo.png',
      size: {
        width: 561,
        height: 294
      }
    },
    regions: [
      {
        name: '都心部',
        code: 'toshin',
        position: {
          wide: { top: '54%', right: '8%' },
          narrow: { top: '57%', right: '1%' }
        }
      },
      {
        name: '23区東部',
        code: '23east',
        position: {
          wide: { top: '28%', right: '2%' },
          narrow: { top: '13%' }
        }
      },
      {
        name: '23区西部',
        code: '23west',
        position: {
          wide: { top: '42%', right: '27%' },
          narrow: { top: '48%', right: '33%' }
        }
      },
      {
        name: '23区外',
        code: 'municipality',
        position: {
          wide: { top: '34%', left: '24%' },
          narrow: { top: '21%', left: '4%' }
        }
      }
    ]
  },

  osaka: {
    image: {
      path: 'https://s3.lifedot.jp/uploads/ending/path/67802/osaka.png',
      size: {
        width: 521,
        height: 363
      }
    },
    regions: [
      {
        name: '大阪',
        code: 'osaka',
        position: {
          wide: { top: '44%', left: '36%' },
          narrow: { top: '38%', left: '23%' }
        }
      },
      {
        name: '北大阪',
        code: 'kitaosaka',
        position: { wide: { top: '16%', right: '30%' }, narrow: { top: '9%' } }
      },
      {
        name: '東大阪',
        code: 'higashiosaka',
        position: {
          wide: { top: '44%', right: '24%' },
          narrow: { top: '38%', right: '14%' }
        }
      },
      {
        name: '南大阪',
        code: 'minamiosaka',
        position: { wide: { top: '70%', right: '40%' }, narrow: { top: '68%' } }
      }
    ]
  },

  okayama: {
    image: {
      path: 'https://s3.lifedot.jp/uploads/ending/path/67804/okayama.png',
      size: {
        width: 640,
        height: 500
      }
    },
    regions: [
      {
        name: '美作',
        code: 'mimasaka',
        position: {
          wide: { top: '24%', right: '34%' },
          narrow: { top: '15%', right: '30%' }
        }
      },
      {
        name: '備前',
        code: 'bizen',
        position: {
          wide: { top: '56%', right: '30%' },
          narrow: { right: '18%' }
        }
      },
      {
        name: '備中',
        code: 'bichu',
        position: { wide: { top: '48%', left: '27%' }, narrow: { left: '15%' } }
      }
    ]
  },

  shizuoka: {
    image: {
      path: 'https://s3.lifedot.jp/uploads/ending/path/67799/shizuoka.png',
      size: {
        width: 640,
        height: 500
      }
    },
    regions: [
      {
        name: '中部',
        code: 'chubu',
        position: {
          wide: { top: '49%', left: '40%' },
          narrow: { top: '45%', left: '36%' }
        }
      },
      {
        name: '西部',
        code: 'seibu',
        position: {
          wide: { top: '66%', left: '19%' },
          narrow: { top: '58%', left: '6%' }
        }
      },
      {
        name: '東部',
        code: 'toubu',
        position: {
          wide: { top: '32%', right: '20%' },
          narrow: { top: '16%', right: '20%' }
        }
      },
      {
        name: '伊豆',
        code: 'izu',
        position: {
          wide: { top: '65%', right: '12%' },
          narrow: { right: '10%' }
        }
      }
    ]
  },

  nara: {
    image: {
      path: 'https://s3.lifedot.jp/uploads/ending/path/67801/nara.png',
      size: {
        width: 640,
        height: 500
      }
    },
    regions: [
      {
        name: '南部',
        code: 'nanbu',
        position: { wide: { top: '64%', left: '40%' }, narrow: { top: '64%' } }
      },
      {
        name: '北部',
        code: 'hokubu',
        position: { wide: { top: '26%', left: '40%' }, narrow: { top: '22%' } }
      }
    ]
  },

  hyogo: {
    image: {
      path: 'https://s3.lifedot.jp/uploads/ending/path/67792/hyogo.png',
      size: {
        width: 640,
        height: 500
      }
    },
    regions: [
      {
        name: '但馬',
        code: 'tajima',
        position: { wide: { top: '12%', left: '43%' }, narrow: { top: '0%' } }
      },
      {
        name: '丹波',
        code: 'tanba',
        position: { wide: { top: '30%', right: '25%' }, narrow: { top: '28%' } }
      },
      {
        name: '播磨',
        code: 'harima',
        position: { wide: { top: '43%', left: '43%' }, narrow: { left: '20%' } }
      },
      {
        name: '摂津',
        code: 'settu',
        position: {
          wide: { top: '55%', right: '18%' },
          narrow: { right: '10%' }
        }
      },
      {
        name: '淡路',
        code: 'awaji',
        position: {
          wide: { top: '80%', right: '38%' },
          narrow: { top: '75%', right: '49%' }
        }
      }
    ]
  },

  tochigi: {
    image: {
      path: 'https://s3.lifedot.jp/uploads/ending/path/67789/tochigi.png',
      size: {
        width: 640,
        height: 500
      }
    },
    regions: [
      {
        name: '日光・那須',
        code: 'nikkoNasu',
        position: {
          wide: { top: '24%', left: '42%' },
          narrow: { top: '14%', left: '35%' }
        }
      },
      {
        name: '県央',
        code: 'kenou',
        position: { wide: { top: '50%', right: '34%' }, narrow: { top: '42%' } }
      },
      {
        name: '県南',
        code: 'kennan',
        position: {
          wide: { top: '70%', left: '36%' },
          narrow: { top: '72%', left: '20%' }
        }
      },
      {
        name: '県東',
        code: 'kentou',
        position: {
          wide: { top: '60%', right: '18%' },
          narrow: { top: '68%', right: '10%' }
        }
      }
    ]
  },

  gunma: {
    image: {
      path: 'https://s3.lifedot.jp/uploads/ending/path/67796/gunma.png',
      size: {
        width: 640,
        height: 500
      }
    },
    regions: [
      {
        name: '利根沼田',
        code: 'tonenumata',
        position: {
          wide: { top: '20%', left: '42%' },
          narrow: { top: '10%', left: '38%' }
        }
      },
      {
        name: '吾妻',
        code: 'agatsuma',
        position: {
          wide: { top: '38%', left: '20%' },
          narrow: { top: '32%', left: '5%' }
        }
      },
      {
        name: '県央',
        code: 'kenou',
        position: {
          wide: { top: '48%', right: '44%' },
          narrow: { right: '40%' }
        }
      },
      {
        name: '東部',
        code: 'toubu',
        position: {
          wide: { top: '70%', right: '20%' },
          narrow: { right: '10%' }
        }
      },
      {
        name: '西部',
        code: 'seibu',
        position: {
          wide: { top: '66%', left: '30%' },
          narrow: { top: '72%', left: '15%' }
        }
      }
    ]
  },

  hiroshima: {
    image: {
      path: 'https://s3.lifedot.jp/uploads/ending/path/67805/hiroshima.png',
      size: {
        width: 640,
        height: 500
      }
    },
    regions: [
      {
        name: '北部',
        code: 'hokubu',
        position: {
          wide: { top: '18%', right: '35%' },
          narrow: { top: '12%', right: '30%' }
        }
      },
      {
        name: '東部',
        code: 'toubu',
        position: {
          wide: { top: '48%', right: '25%' },
          narrow: { right: '15%' }
        }
      },
      {
        name: '西部',
        code: 'seibu',
        position: {
          wide: { top: '50%', left: '25%' },
          narrow: { top: '45%', left: '18%' }
        }
      }
    ]
  },

  miyagi: {
    image: {
      path: 'https://s3.lifedot.jp/uploads/ending/path/67791/miyagi.png',
      size: {
        width: 640,
        height: 500
      }
    },
    regions: [
      {
        name: '三陸・県北',
        code: 'sanrikuKenhoku',
        position: {
          wide: { top: '28%', right: '33%' },
          narrow: { top: '15%', right: '15%' }
        }
      },
      {
        name: '仙台',
        code: 'sendai',
        position: {
          wide: { top: '50%', right: '48%' },
          narrow: { top: '42%', right: '42%' }
        }
      },
      {
        name: '県南',
        code: 'kennan',
        position: { wide: { top: '72%', left: '30%' }, narrow: { left: '16%' } }
      }
    ]
  },

  aichi: {
    image: {
      path: 'https://s3.lifedot.jp/uploads/ending/path/67793/aichi.png',
      size: {
        width: 640,
        height: 500
      }
    },
    regions: [
      {
        name: '名古屋',
        code: 'nagoya',
        position: { wide: { top: '24%', left: '24%' }, narrow: { top: '16%' } }
      },
      {
        name: '尾張',
        code: 'owari',
        position: { wide: { top: '45%', left: '20%' }, narrow: { left: '4%' } }
      },
      {
        name: '西三河',
        code: 'nishimikawa',
        position: { wide: { top: '45%', right: '37%' } }
      },
      {
        name: '東三河',
        code: 'higashimikawa',
        position: {
          wide: { top: '55%', right: '15%' },
          narrow: { right: '5%' }
        }
      }
    ]
  },

  ibaraki: {
    image: {
      path: 'https://s3.lifedot.jp/uploads/ending/path/67800/ibaraki.png',
      size: {
        width: 640,
        height: 500
      }
    },
    regions: [
      {
        name: '県北',
        code: 'kenhoku',
        position: { wide: { top: '20%', left: '52%' }, narrow: { top: '10%' } }
      },
      {
        name: '県央',
        code: 'kenou',
        position: { wide: { top: '46%', right: '40%' }, narrow: { top: '38%' } }
      },
      {
        name: '県西',
        code: 'kensei',
        position: {
          wide: { top: '60%', left: '20%' },
          narrow: { top: '50%', left: '5%' }
        }
      },
      {
        name: '県南',
        code: 'kennan',
        position: {
          wide: { top: '65%', right: '48%' },
          narrow: { top: '70%', right: '40%' }
        }
      },
      {
        name: '鹿行',
        code: 'rokkou',
        position: {
          wide: { top: '70%', right: '30%' },
          narrow: { right: '8%' }
        }
      }
    ]
  },

  kyoto: {
    image: {
      path: 'https://s3.lifedot.jp/uploads/ending/path/67790/kyoto.png',
      size: {
        width: 640,
        height: 500
      }
    },
    regions: [
      {
        name: '京都市',
        code: 'kyoto',
        position: {
          wide: { top: '52%', right: '24%' },
          narrow: { top: '42%', right: '10%' }
        }
      },
      {
        name: '北部',
        code: 'hokubu',
        position: { wide: { top: '26%', left: '28%' }, narrow: { top: '13%' } }
      },
      {
        name: '南部',
        code: 'nanbu',
        position: { wide: { top: '66%', left: '44%' }, narrow: { left: '34%' } }
      }
    ]
  },

  saitama: {
    image: {
      path: 'https://s3.lifedot.jp/uploads/ending/path/67803/saitama.png',
      size: {
        width: 640,
        height: 500
      }
    },
    regions: [
      {
        name: '北部',
        code: 'hokubu',
        position: {
          wide: { top: '25%', left: '37%' },
          narrow: { top: '12%', left: '30%' }
        }
      },
      {
        name: '西部',
        code: 'seibu',
        position: {
          wide: { top: '52%', left: '44%' },
          narrow: { top: '48%', left: '36%' }
        }
      },
      {
        name: '東部',
        code: 'toubu',
        position: {
          wide: { top: '32%', right: '18%' },
          narrow: { right: '10%' }
        }
      },
      {
        name: '中央',
        code: 'chuou',
        position: {
          wide: { top: '58%', right: '20%' },
          narrow: { top: '60%', right: '5%' }
        }
      },
      {
        name: '秩父',
        code: 'chichibu',
        position: {
          wide: { top: '48%', left: '15%' },
          narrow: { top: '41%', left: '5%' }
        }
      }
    ]
  },

  fukuoka: {
    image: {
      path: 'https://s3.lifedot.jp/uploads/ending/path/67798/fukuoka.png',
      size: {
        width: 640,
        height: 500
      }
    },
    regions: [
      {
        name: '福岡',
        code: 'fukuoka',
        position: {
          wide: { top: '38%', left: '23%' },
          narrow: { top: '20%', left: '8%' }
        }
      },
      {
        name: '北九州',
        code: 'kitakyushu',
        position: {
          wide: { top: '22%', right: '19%' },
          narrow: { top: '14%', right: '5%' }
        }
      },
      {
        name: '筑豊',
        code: 'chikuhou',
        position: { wide: { top: '35%', right: '40%' } }
      },
      {
        name: '筑後',
        code: 'chikugo',
        position: {
          wide: { top: '70%', right: '45%' },
          narrow: { right: '43%' }
        }
      }
    ]
  },

  kanagawa: {
    image: {
      path: 'https://s3.lifedot.jp/uploads/ending/path/67797/kanagawa.png',
      size: {
        width: 640,
        height: 500
      }
    },
    regions: [
      {
        name: '横浜',
        code: 'yokohama',
        position: {
          wide: { top: '33%', right: '18%' },
          narrow: { right: '10%' }
        }
      },
      {
        name: '川崎',
        code: 'kawasaki',
        position: {
          wide: { top: '10%', right: '8%' },
          narrow: { top: '5%', right: '5%' }
        }
      },
      {
        name: '横須賀三浦',
        code: 'yokosukamiura',
        position: { wide: { top: '70%', right: '0%' } }
      },
      {
        name: '県央',
        code: 'kenou',
        position: {
          wide: { top: '25%', right: '48%' },
          narrow: { top: '12%', right: '42%' }
        }
      },
      {
        name: '湘南',
        code: 'shonan',
        position: { wide: { top: '52%', right: '40%' }, narrow: { top: '42%' } }
      },
      {
        name: '県西',
        code: 'kensei',
        position: { wide: { top: '60%', left: '20%' }, narrow: { left: '5%' } }
      }
    ]
  },

  chiba: {
    image: {
      path: 'https://s3.lifedot.jp/uploads/ending/path/67795/chiba.png',
      size: {
        width: 640,
        height: 500
      }
    },
    regions: [
      {
        name: '北西部',
        code: 'hokuseibu',
        position: {
          wide: { top: '28%', left: '28%' },
          narrow: { top: '20%', left: '26%' }
        }
      },
      {
        name: '北東部',
        code: 'hokutoubu',
        position: {
          wide: { top: '45%', right: '26%' },
          narrow: { right: '17%' }
        }
      },
      {
        name: '南部',
        code: 'nanbu',
        position: { wide: { top: '72%', left: '35%' } }
      }
    ]
  }
}
